import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { userAppData } from '../MainApp.js';
import { SEC_ApiCall } from '../func.js';
import Header from '../common/header.js';
import LoadingComp from '../common/loading.js';
import { FaArrowLeft, FaTools } from 'react-icons/fa';
import '../sysManager/SysManagerDesktop.css';
import '../sysManager/SysManagerMobile.css';
import './SysManagerTools.css';
import { EMAIL_SIGNATURE_TOOL_GUID, TOOL_REGISTRY } from './toolRegistry.js';

/** Refreshes the school catalogue on entry and requires current Tools administration access. */
export default function SysManagerTools() {
    const systemManager = userAppData?.sysManager || {};
    const hasAccess = Number(systemManager.perms ?? 0) === 1 && systemManager.toolsAdmin === true;
    const [tools, setTools] = useState([]);
    const [status, setStatus] = useState('loading');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!hasAccess) return undefined;
        let active = true;
        // Read fresh permissions so removed administration access cannot expose the catalogue.
        async function loadTools() {
            setStatus('loading');
            setTools([]);
            try {
                const response = await SEC_ApiCall('perms/');
                if (!active) return;
                const access = response?.sysManagerData;
                if (Number(access?.perms ?? 0) !== 1 || access?.toolsAdmin !== true) {
                    setStatus('denied');
                    return;
                }
                const available = response?.toolsData?.availableTools;
                if (!Array.isArray(available)) throw new Error('Missing school tools catalogue');
                const catalogue = available.filter(tool => typeof tool?.guid === 'string' && typeof tool?.name === 'string');
                // The signature builder creates its own catalogue entry on first save.
                if (!catalogue.some(tool => tool.guid === EMAIL_SIGNATURE_TOOL_GUID)) {
                    catalogue.push({ guid: EMAIL_SIGNATURE_TOOL_GUID, name: TOOL_REGISTRY[EMAIL_SIGNATURE_TOOL_GUID].name });
                }
                setTools(catalogue);
                setStatus('ready');
            } catch (error) {
                if (active) setStatus('error');
            }
        }
        loadTools();
        return () => { active = false; };
    }, [hasAccess, attempt]);

    if (!hasAccess || status === 'denied') return <Navigate to='/app' replace />;

    return (
        <div className='main_div'>
            <div className='internal_main_div sysManager_home_container sysManager_tools_container'>
                <Header
                    title='Tools'
                    subtitle='Manage access to the Tools module and the tools available for your school.'
                    rightContent={<>
                        <Link className='module-page-header__button' to='/app/sysmanager/tools/access'>Manage Tools Access</Link>
                        <Link className='module-page-header__button' to='/app/sysmanager'>
                            <FaArrowLeft aria-hidden='true' /> Back to System Manager
                        </Link>
                    </>}
                />
                {status === 'loading' && <LoadingComp />}
                {status === 'error' && <div role='alert' className='sysManager_tools_status'>
                    <p>School tools could not be loaded. Please try again.</p>
                    <button className='module-page-header__button' type='button' onClick={() => setAttempt(value => value + 1)}>Try again</button>
                </div>}
                {status === 'ready' && (tools.length === 0
                    ? <p className='sysManager_tools_status'>No tools are available for this school yet.</p>
                    : <ul className='sysManager_main_div sysManager_tools_catalogue' aria-label='School tools'>
                        {tools.map(tool => {
                            // Only registered administration routes are offered as clickable cards.
                            const adminPath = Object.prototype.hasOwnProperty.call(TOOL_REGISTRY, tool.guid)
                                ? TOOL_REGISTRY[tool.guid].adminPath : null;
                            const ToolIcon = (adminPath && TOOL_REGISTRY[tool.guid].icon) || FaTools;
                            const content = <>
                                <ToolIcon className='sysManager_tools_icon' aria-hidden='true' />
                                <span className='sysManager_tools_copy'>
                                    <span className='sysManager_tools_name'>{tool.name}</span>
                                    <span className='sysManager_tools_description'>{tool.guid === EMAIL_SIGNATURE_TOOL_GUID ? 'Build the template and manage access' : adminPath ? 'Manage staff access' : 'Configuration coming soon'}</span>
                                </span>
                            </>;
                            return <li key={tool.guid}>
                                {adminPath
                                    ? <Link className='sysManager_tools_card' to={adminPath}>{content}</Link>
                                    : <div className='sysManager_tools_card sysManager_tools_card--static'>{content}</div>}
                            </li>;
                        })}
                    </ul>)}
            </div>
        </div>
    );
}
